import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from '../lib/api';

const STORAGE_KEY = 'riser-mail.session';

interface StoredSession {
  readonly token: string;
  readonly expiresAt: number;
}

/**
 * Reads the saved session, discarding one that has already expired.
 *
 * localStorage throws outright in a browser configured to block site data, so
 * every access is guarded — an unreadable store should mean "log in again",
 * not a blank page.
 */
function loadSession(): StoredSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (typeof parsed.token !== 'string' || typeof parsed.expiresAt !== 'number') return null;

    // The server checks this too and is the authority. Checking here as well
    // just avoids one guaranteed-401 round trip on every cold load.
    return parsed.expiresAt > Date.now() ? { token: parsed.token, expiresAt: parsed.expiresAt } : null;
  } catch {
    return null;
  }
}

function saveSession(session: StoredSession | null): void {
  try {
    if (session) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // A session that cannot be persisted still works for this tab. Failing the
    // login over it would be worse than losing it on refresh.
  }
}

export interface SessionState {
  readonly token: string | null;
  readonly signingIn: boolean;
  readonly error: string | null;
  readonly signIn: (password: string) => Promise<void>;
  readonly signOut: () => void;
}

/**
 * Holds the session token and keeps it in sync with localStorage.
 *
 * The token rather than the password is stored, so a future XSS bug leaks
 * something that expires and that rotating SESSION_SECRET can revoke.
 */
export function useSession(): SessionState {
  const [session, setSession] = useState<StoredSession | null>(loadSession);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    saveSession(session);
  }, [session]);

  const signIn = useCallback(async (password: string): Promise<void> => {
    setSigningIn(true);
    setError(null);

    try {
      setSession(await api.login(password));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not reach the server.');
    } finally {
      setSigningIn(false);
    }
  }, []);

  const signOut = useCallback((): void => {
    setSession(null);
    setError(null);
  }, []);

  return { token: session?.token ?? null, signingIn, error, signIn, signOut };
}
